import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FJAppConstant from '../constants/FJAppConstant';
import { useCallback } from 'react';
import FJActionAiHistoryCommon from '../redux/action/FJActionAiHistoryCommon';
import FJLocalStore from '../store/FJLocalStore';
import FJHeaderConfig from '../config/FJHeaderConfig';
import FJRequirementsUtil from '../util/FJRequirementsUtil';

const useAIListSideModelData = () => {
    const dispatch = useDispatch();
    const aiVideoHistoryList = useSelector(state => state.aiHistoryCommon.aiVideoHistoryList);
    const aiVideoHistoryLoadStatus = useSelector(state => state.aiHistoryCommon.aiVideoHistoryLoadStatus);
    const aiTextToImageHistoryList = useSelector(state => state.aiHistoryCommon.aiTextToImageHistoryList);
    const aiTextToImageHistoryLoadStatus = useSelector(state => state.aiHistoryCommon.aiTextToImageHistoryLoadStatus);
    const aiImageToImageHistoryList = useSelector(state => state.aiHistoryCommon.aiImageToImageHistoryList);
    const aiImageToImageHistoryLoadStatus = useSelector(state => state.aiHistoryCommon.aiImageToImageHistoryLoadStatus);
    const active = useSelector(state => state.aiHistoryCommon.isShowAIImageHistoryModal);
    const appType = useSelector(state => state.common.appType);

    const curLanguage = FJLocalStore.currentTag();

    const formatList = useCallback((historyList, isVideo = false) => {
        if (historyList.length === 0) {
            return {
                imageList: [],
                sizeList: [],
            };
        }
        const sizeList = [];
        const imageList = [];

        historyList.forEach(item => {
            let aspectRatio = 1;
            if (item.width && item.height) {
                aspectRatio = item.width / item.height;
            }

            if (item.ratio) {
                aspectRatio = parseFloat(item.ratio);
            }

            imageList.push({
                imgUrl: item.imgUrl,
                id: item.id,
            });

            if (isVideo) {
                imageList[imageList.length - 1].videoUrl = item.videoUrl;
                imageList[imageList.length - 1].generationType = item.generationType;
            }

            sizeList.push({
                width: 166,
                height: 166 / aspectRatio,
            });
        });

        return {
            imageList,
            sizeList,
        };
    }, []);

    const getHref = useCallback(
        appType => {
            const href = FJHeaderConfig.aiMenuList.find(item => item.title === appType).href;
            return `${curLanguage === 'en' ? '' : '/' + curLanguage}${href}`;
        },
        [curLanguage],
    );

    const list = useMemo(() => {
        switch (appType) {
            case FJAppConstant.AI_VIDEO_GENERATOR: {
                const textToImageHref = getHref('ai-text-to-image');
                const imageToImageHref = getHref('ai-image-to-image');
                const { imageList: textImageList, sizeList: textImageSizeList } = formatList(aiTextToImageHistoryList);
                const { imageList: imageToImageList, sizeList: imageToImageSizeList } =
                    formatList(aiImageToImageHistoryList);

                return [
                    {
                        imageList: textImageList,
                        sizeList: textImageSizeList,
                        historyType: FJAppConstant.TEXT_TO_IMAGE,
                        loadStatus: aiTextToImageHistoryLoadStatus,
                        title: 'text-to-image',
                        onRetry: () => {
                            dispatch(FJActionAiHistoryCommon.initAiTextToImageHistoryList());
                        },
                        onGenerateClick: () => {
                            window.open(textToImageHref);
                        },
                    },
                    {
                        imageList: imageToImageList,
                        sizeList: imageToImageSizeList,
                        historyType: FJAppConstant.IMAGE_TO_IMAGE,
                        loadStatus: aiImageToImageHistoryLoadStatus,
                        title: 'image-to-image',
                        onRetry: () => {
                            dispatch(FJActionAiHistoryCommon.initAiImageToImageHistoryList());
                        },
                        onGenerateClick: () => {
                            window.open(imageToImageHref);
                        },
                    },
                ];
            }
            case FJAppConstant.AI_VIDEO_DUB: {
                const aiVideoHref = getHref('ai-video-generator');
                const textToVideoHref = aiVideoHref;
                const imageToVideoHref = aiVideoHref + '?tab_type=iv';
                const textToVideoList = FJRequirementsUtil.getSortHistoryList(aiVideoHistoryList, 'text-to-video');
                const imageToVideoList = FJRequirementsUtil.getSortHistoryList(aiVideoHistoryList, 'image-to-video');

                const { imageList: textToVideoImageList, sizeList: textToVideoSizeList } = formatList(
                    textToVideoList,
                    true,
                );
                const { imageList: imageToVideoImageList, sizeList: imageToVideoSizeList } = formatList(
                    imageToVideoList,
                    true,
                );

                return [
                    {
                        imageList: textToVideoImageList,
                        sizeList: textToVideoSizeList,
                        loadStatus: aiVideoHistoryLoadStatus,
                        title: 'text-to-video',
                        historyType: FJAppConstant.TEXT_TO_VIDEO,
                        onRetry: () => {
                            dispatch(FJActionAiHistoryCommon.initAiVideoHistoryList());
                        },
                        onGenerateClick: () => {
                            window.open(textToVideoHref);
                        },
                    },
                    {
                        imageList: imageToVideoImageList,
                        sizeList: imageToVideoSizeList,
                        loadStatus: aiVideoHistoryLoadStatus,
                        title: 'image-to-video',
                        historyType: FJAppConstant.IMAGE_TO_VIDEO,
                        onRetry: () => {
                            dispatch(FJActionAiHistoryCommon.initAiVideoHistoryList());
                        },
                        onGenerateClick: () => {
                            window.open(imageToVideoHref);
                        },
                    },
                ];
            }
            default:
                return [];
        }
    }, [
        appType,
        aiVideoHistoryList,
        aiVideoHistoryLoadStatus,
        aiTextToImageHistoryList,
        aiTextToImageHistoryLoadStatus,
        aiImageToImageHistoryList,
        aiImageToImageHistoryLoadStatus,
        dispatch,
        formatList,
        getHref,
    ]);

    const showModal = useCallback(() => {
        dispatch(FJActionAiHistoryCommon.setIsShowAIImageHistoryModalAction(true));

        switch (appType) {
            case FJAppConstant.AI_VIDEO_DUB: {
                dispatch(FJActionAiHistoryCommon.initAiVideoHistoryList());
                break;
            }
            default:
                break;
        }
    }, [dispatch, appType]);

    const closeModal = useCallback(() => {
        dispatch(FJActionAiHistoryCommon.setIsShowAIImageHistoryModalAction(false));
    }, [dispatch]);

    return {
        list,
        isShow: active,
        showModal,
        closeModal,
    };
};

export default useAIListSideModelData;
